/**
 * Thin TypeScript wrapper over engine.wasm.
 *
 * Thin is the contract: this file loads the module and hands JSON across. It
 * does not interpret specs, compute geometry, or format anything — all of that
 * lives in the engine so the browser and the PDF worker share one implementation.
 */

/// <reference path="./generated.d.ts" />
// The reference is what lets a consumer type-check this file without knowing that
// `../generated/engine.js` is a build artifact it may not have on disk yet.

import type { Plan, SpecInput } from "./types.js";

export * from "./types.js";
export { toBoardFeet, toFeetInches, toInches } from "./format.js";

export interface Engine {
  /** A JSON spec in, a resolved-model JSON out. The raw seam. */
  buildPlan(specJson: string): string;
  /**
   * The same call, typed. Serializing and parsing is not interpreting — every
   * decision about what the JSON means still belongs to the engine.
   */
  plan(spec: SpecInput): Plan;
  /** The engine's semver, half of the output contract with the spec's version. */
  readonly version: string;
}

let pending: Promise<Engine> | undefined;

/**
 * Loads (and caches) the engine module. Instantiation is async because WASM
 * compilation is; every call after the first resolves from the same promise.
 */
export function loadEngine(): Promise<Engine> {
  pending ??= import("../generated/engine.js").then(async (mod) => {
    const instance = await mod.default();
    const buildPlan = (specJson: string): string => instance.buildPlan(specJson);
    return {
      buildPlan,
      plan: (spec: SpecInput): Plan => JSON.parse(buildPlan(JSON.stringify(spec))) as Plan,
      version: instance.engineVersion,
    };
  });
  return pending;
}

/** Drops the cached instance. Tests use this; application code should not need it. */
export function resetEngine(): void {
  pending = undefined;
}
